import type {
  SessionHeapEntry,
  SessionHeapList,
  SessionHeapSnapshot,
} from "./types";
import type { FeedItem } from "./feed";

const ENTRY_KEY_CANDIDATES = ["entryPath", "path"] as const;
const ENTRY_ARRAY_KEY_CANDIDATES = ["entryPaths", "paths"] as const;
const LIST_KEY_CANDIDATES = ["listName"] as const;
const LIST_ARRAY_KEY_CANDIDATES = ["listNames"] as const;
const VARIABLE_KEY_CANDIDATES = ["variableName"] as const;
const VARIABLE_ARRAY_KEY_CANDIDATES = ["variableNames"] as const;
const UI_CONTAINER_KEYS = ["show", "display", "present", "ui"] as const;

export interface JobPresentation {
  /** Latest canonical assistant message for this job by server sequence. */
  responseText: string | null;
  entries: SessionHeapEntry[];
  lists: SessionHeapList[];
  changedEntries: SessionHeapEntry[];
  changedLists: SessionHeapList[];
  hasExplicitArtifacts: boolean;
}

interface HeapReferences {
  entryPaths: Set<string>;
  listNames: Set<string>;
  variableNames: Set<string>;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function responseTextFromFeedItems(
  feedItems: readonly FeedItem[],
  jobId: string,
): string | null {
  let latestSequence = -1;
  let latestText: string | null = null;

  for (const item of feedItems) {
    if (
      item.kind !== "message" ||
      item.payload.role !== "assistant" ||
      item.source?.jobId !== jobId ||
      item.sequence <= latestSequence
    ) {
      continue;
    }
    const text = item.payload.text.trim();
    if (!text) continue;
    latestSequence = item.sequence;
    latestText = text;
  }

  return latestText;
}

function pushString(target: Set<string>, value: unknown) {
  if (typeof value === "string" && value.trim()) {
    target.add(value.trim());
  }
}

function pushStringArray(target: Set<string>, value: unknown) {
  if (!Array.isArray(value)) return;
  for (const item of value) {
    pushString(target, item);
  }
}

function collectReferencesFromRecord(
  record: Record<string, unknown>,
  refs: HeapReferences,
) {
  for (const key of ENTRY_KEY_CANDIDATES)
    pushString(refs.entryPaths, record[key]);
  for (const key of ENTRY_ARRAY_KEY_CANDIDATES)
    pushStringArray(refs.entryPaths, record[key]);
  for (const key of LIST_KEY_CANDIDATES)
    pushString(refs.listNames, record[key]);
  for (const key of LIST_ARRAY_KEY_CANDIDATES)
    pushStringArray(refs.listNames, record[key]);
  for (const key of VARIABLE_KEY_CANDIDATES)
    pushString(refs.variableNames, record[key]);
  for (const key of VARIABLE_ARRAY_KEY_CANDIDATES)
    pushStringArray(refs.variableNames, record[key]);
}

function stringValue(
  record: Record<string, unknown>,
  keys: string[],
): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function findEntryPathForRecord(
  record: Record<string, unknown>,
  heap: SessionHeapSnapshot,
): string | null {
  const directPath = stringValue(record, ["entryPath", "path"]);
  if (directPath && heap.entriesByPath?.[directPath]) {
    return directPath;
  }

  const id = stringValue(record, ["id", "_id", "recordId", "objectId"]);
  if (!id) {
    return null;
  }
  const className = stringValue(record, [
    "className",
    "_className",
    "__className",
    "prototype",
    "type",
  ]);
  const entries = Object.values(heap.entriesByPath || {});
  const exact = entries.find(
    (entry) =>
      entry.id === id &&
      (!className ||
        entry.className === className ||
        entry.prototypes?.includes(className)),
  );
  if (exact?.path) {
    return exact.path;
  }

  const idOnlyMatches = entries.filter((entry) => entry.id === id);
  return idOnlyMatches.length === 1 ? idOnlyMatches[0]!.path : null;
}

function scanForHeapReferences(
  value: unknown,
  heap: SessionHeapSnapshot,
  refs: HeapReferences,
  depth = 0,
  seen = new Set<unknown>(),
) {
  if (value === null || value === undefined || depth > 4 || seen.has(value))
    return;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (heap.entriesByPath?.[trimmed]) refs.entryPaths.add(trimmed);
    if (heap.listsByName?.[trimmed]) refs.listNames.add(trimmed);
    if (heap.variablesByName?.[trimmed]) refs.variableNames.add(trimmed);
    return;
  }

  if (Array.isArray(value)) {
    seen.add(value);
    for (const item of value.slice(0, 24)) {
      scanForHeapReferences(item, heap, refs, depth + 1, seen);
    }
    return;
  }

  const record = asRecord(value);
  if (!record) return;
  seen.add(value);
  const entryPath = findEntryPathForRecord(record, heap);
  if (entryPath) refs.entryPaths.add(entryPath);
  collectReferencesFromRecord(record, refs);
  for (const key of UI_CONTAINER_KEYS) {
    const nested = asRecord(record[key]);
    if (nested) collectReferencesFromRecord(nested, refs);
  }
  for (const nested of Object.values(record).slice(0, 24)) {
    scanForHeapReferences(nested, heap, refs, depth + 1, seen);
  }
}

function resolveVariablesToReferences(
  variableNames: Iterable<string>,
  heap: SessionHeapSnapshot,
  refs: HeapReferences,
) {
  for (const variableName of variableNames) {
    const variable = heap.variablesByName?.[variableName];
    if (!variable) continue;
    if (variable.kind === "entry" && variable.entryPath) {
      refs.entryPaths.add(variable.entryPath);
    }
    if (variable.kind === "list" && variable.listName) {
      refs.listNames.add(variable.listName);
    }
  }
}

function sortEntries(entries: SessionHeapEntry[]): SessionHeapEntry[] {
  return [...entries].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

function sortLists(lists: SessionHeapList[]): SessionHeapList[] {
  return [...lists].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

function dedupeEntries(entries: SessionHeapEntry[]): SessionHeapEntry[] {
  const seen = new Set<string>();
  const result: SessionHeapEntry[] = [];
  for (const entry of entries) {
    if (!entry?.path || seen.has(entry.path)) continue;
    seen.add(entry.path);
    result.push(entry);
  }
  return result;
}

function dedupeLists(lists: SessionHeapList[]): SessionHeapList[] {
  const seen = new Set<string>();
  const result: SessionHeapList[] = [];
  for (const list of lists) {
    if (!list?.name || seen.has(list.name)) continue;
    seen.add(list.name);
    result.push(list);
  }
  return result;
}

function getJobRelatedEntries(
  heap: SessionHeapSnapshot,
  jobId: string,
): SessionHeapEntry[] {
  return sortEntries(
    Object.values(heap.entriesByPath || {}).filter((entry) =>
      entry.relatedJobIds?.includes(jobId),
    ),
  );
}

function getJobRelatedLists(
  heap: SessionHeapSnapshot,
  jobId: string,
): SessionHeapList[] {
  return sortLists(
    Object.values(heap.listsByName || {}).filter((list) =>
      list.relatedJobIds?.includes(jobId),
    ),
  );
}

function entriesFromLists(
  lists: SessionHeapList[],
  heap: SessionHeapSnapshot,
): SessionHeapEntry[] {
  const entries: SessionHeapEntry[] = [];
  for (const list of lists) {
    for (const path of list.paths || []) {
      const entry = heap.entriesByPath?.[path];
      if (entry) entries.push(entry);
    }
  }
  return entries;
}

export function resolveJobPresentation({
  jobId,
  result,
  feedItems = [],
  sessionHeap,
  allowExplicitArtifacts = true,
}: {
  jobId: string;
  result: unknown;
  feedItems?: readonly FeedItem[];
  sessionHeap: SessionHeapSnapshot;
  allowExplicitArtifacts?: boolean;
}): JobPresentation {
  const refs: HeapReferences = {
    entryPaths: new Set<string>(),
    listNames: new Set<string>(),
    variableNames: new Set<string>(),
  };

  if (allowExplicitArtifacts) {
    scanForHeapReferences(result, sessionHeap, refs);
    resolveVariablesToReferences(refs.variableNames, sessionHeap, refs);
  }

  const referencedLists = sortLists(
    [...refs.listNames]
      .map((name) => sessionHeap.listsByName?.[name])
      .filter((list): list is SessionHeapList => Boolean(list)),
  );
  const referencedEntries = sortEntries(
    [...refs.entryPaths]
      .map((path) => sessionHeap.entriesByPath?.[path])
      .filter((entry): entry is SessionHeapEntry => Boolean(entry)),
  );

  const jobLists = getJobRelatedLists(sessionHeap, jobId);
  const jobEntries = getJobRelatedEntries(sessionHeap, jobId);
  const changedEntries = dedupeEntries([
    ...jobEntries,
    ...entriesFromLists(jobLists, sessionHeap),
  ]);
  const explicitLists = dedupeLists(referencedLists);
  const explicitEntries = dedupeEntries([
    ...referencedEntries,
    ...entriesFromLists(referencedLists, sessionHeap),
  ]);
  const hasExplicitArtifacts =
    allowExplicitArtifacts &&
    (explicitEntries.length > 0 || explicitLists.length > 0);

  const lists = hasExplicitArtifacts ? explicitLists : jobLists;
  const entries = hasExplicitArtifacts ? explicitEntries : changedEntries;

  return {
    responseText: responseTextFromFeedItems(feedItems, jobId),
    entries,
    lists,
    changedEntries,
    changedLists: jobLists,
    hasExplicitArtifacts,
  };
}
